/**
 * The shareable delivery card (docs/refactor/41 §4.2): what a session delivered, as a 1200×630 image the person can
 * paste in a chat or a post. Drawn here from the metadata the screen already shows — subject, files, lines, tests,
 * cost, duration, agent — never code, never a diff. Opt-in: it only exists when the person clicks Compartilhar.
 */

import { useEffect, useRef, useState } from 'react'
import { Copy, Download } from 'lucide-react'
import { useI18n, type Locale } from '../lib/i18n'
import { formatCost, formatDuration } from '../lib/format'
import type { WorkSessionDetail } from '../lib/types'
import { Modal } from './Modal'
import mark from '../assets/vephus-mark.png'

const W = 1200
const H = 630

export function DeliveryCard({ detail, onClose }: { detail: WorkSessionDetail; onClose: () => void }) {
  const { t, locale } = useI18n()
  const canvas = useRef<HTMLCanvasElement>(null)
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')

  useEffect(() => {
    const element = canvas.current
    if (!element) return
    let context: CanvasRenderingContext2D | null = null
    try {
      context = element.getContext('2d')
    } catch {
      context = null
    }
    if (!context) return
    draw(context, detail, t, locale)
    const image = new Image()
    image.onload = () => {
      context!.drawImage(image, 56, 48, 72, 72)
    }
    image.src = mark
  }, [detail, t, locale])

  const toBlob = () =>
    new Promise<Blob | null>((resolve) => {
      try {
        canvas.current?.toBlob((blob) => resolve(blob), 'image/png')
      } catch {
        resolve(null)
      }
    })

  const copy = async () => {
    const blob = await toBlob()
    try {
      if (!blob || !navigator.clipboard || typeof ClipboardItem === 'undefined') throw new Error('no-clipboard')
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
      setState('copied')
    } catch {
      setState('failed')
    }
  }

  const download = async () => {
    const blob = await toBlob()
    if (!blob) {
      setState('failed')
      return
    }
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = 'vephus-entrega.png'
    anchor.click()
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }

  return (
    <Modal title={t('share.title')} onClose={onClose} wide>
      <div className="stack">
        <canvas ref={canvas} width={W} height={H} className="share-card" aria-label={t('share.title')} />
        <span className="tiny muted">{t('share.hint')}</span>
        <div className="row row--tight">
          <button type="button" className="btn btn--primary btn--sm" onClick={() => void copy()}>
            <Copy size={13} /> {state === 'copied' ? t('share.copied') : t('share.copy')}
          </button>
          <button type="button" className="btn btn--sm" onClick={() => void download()}>
            <Download size={13} /> {t('share.download')}
          </button>
          {state === 'failed' && <span className="tiny muted">{t('share.failed')}</span>}
        </div>
      </div>
    </Modal>
  )
}

type Translate = (key: string, vars?: Record<string, string | number>) => string

function draw(c: CanvasRenderingContext2D, detail: WorkSessionDetail, t: Translate, locale: Locale) {
  const { session, codeChanges, testRuns, aiUsage } = detail

  // The app's own dark, with a soft orange glow top-left and a hairline border.
  c.fillStyle = '#0d1016'
  c.fillRect(0, 0, W, H)
  const glow = c.createRadialGradient(140, 90, 10, 140, 90, 640)
  glow.addColorStop(0, 'rgba(249, 115, 22, 0.16)')
  glow.addColorStop(1, 'rgba(249, 115, 22, 0)')
  c.fillStyle = glow
  c.fillRect(0, 0, W, H)
  c.strokeStyle = 'rgba(255,255,255,0.14)'
  c.lineWidth = 2
  c.strokeRect(1, 1, W - 2, H - 2)

  const sans = (size: number, weight = 400) => `${weight} ${size}px "Segoe UI", system-ui, sans-serif`

  // Header: the mark (drawn by the caller when it loads), the name, the date.
  c.fillStyle = '#ffffff'
  c.font = sans(34, 700)
  c.fillText('Vephus', 144, 94)
  c.fillStyle = 'rgba(255,255,255,0.55)'
  c.font = sans(22)
  const when = new Date(session.lastActivityAt).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' })
  c.textAlign = 'right'
  c.fillText(when, W - 56, 88)
  c.textAlign = 'left'

  // The delivery: what, where, by which agent.
  c.fillStyle = 'rgba(249, 115, 22, 0.95)'
  c.font = sans(21, 600)
  c.fillText(t('share.delivered').toUpperCase(), 56, 188)
  c.fillStyle = '#ffffff'
  c.font = sans(44, 700)
  const subject = session.taskTitle ?? session.repositoryName ?? t('share.untitled')
  wrap(c, subject, 56, 242, W - 112, 54, 2)
  c.fillStyle = 'rgba(255,255,255,0.65)'
  c.font = sans(24)
  const agent = [session.providerName, session.modelName].filter(Boolean).join(' · ')
  const line = [session.repositoryName, session.branch, agent, session.developerName].filter(Boolean).join('  ·  ')
  c.fillText(clip(line, 92), 56, 332)

  // The numbers, each in its quiet box.
  const passed = testRuns.reduce((sum, run) => sum + run.passed, 0)
  const total = testRuns.reduce((sum, run) => sum + run.total, 0)
  const stats: Array<[string, string]> = [
    [t('share.files'), String(codeChanges.filesChanged)],
    [t('share.lines'), `+${codeChanges.linesAdded} −${codeChanges.linesDeleted}`],
    [t('share.tests'), total > 0 ? `${passed}/${total}` : '—'],
    [t('share.cost'), aiUsage.accuracy === 'Unavailable' ? '—' : formatCost(aiUsage.costUsd, locale)],
    [t('share.time'), formatDuration(session.durationSeconds, locale)],
  ]
  const boxW = (W - 112 - 4 * 16) / 5
  stats.forEach(([label, value], i) => {
    const x = 56 + i * (boxW + 16)
    c.fillStyle = 'rgba(255,255,255,0.05)'
    c.strokeStyle = 'rgba(255,255,255,0.12)'
    roundRect(c, x, 392, boxW, 128, 14)
    c.fill()
    c.stroke()
    c.fillStyle = 'rgba(255,255,255,0.55)'
    c.font = sans(19)
    c.fillText(label, x + 20, 432)
    c.fillStyle = '#ffffff'
    c.font = sans(34, 700)
    c.fillText(clip(value, 12), x + 20, 482)
  })

  // Footer.
  c.fillStyle = 'rgba(255,255,255,0.45)'
  c.font = sans(21)
  c.fillText(t('share.footer'), 56, H - 52)
  c.fillStyle = 'rgba(249, 115, 22, 0.95)'
  c.textAlign = 'right'
  c.font = sans(21, 600)
  c.fillText('vephus.ai', W - 56, H - 52)
  c.textAlign = 'left'
}

function wrap(c: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number, maxLines: number) {
  const words = text.split(/\s+/)
  let line = ''
  let lines = 0
  for (const word of words) {
    const attempt = line ? `${line} ${word}` : word
    if (c.measureText(attempt).width > maxWidth && line) {
      lines += 1
      if (lines === maxLines) {
        c.fillText(clip(line, 999) + '…', x, y)
        return
      }
      c.fillText(line, x, y)
      y += lineHeight
      line = word
    } else {
      line = attempt
    }
  }
  if (line) c.fillText(line, x, y)
}

function clip(text: string, max: number): string {
  return text.length > max ? text.slice(0, max - 1) + '…' : text
}

function roundRect(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  c.beginPath()
  c.moveTo(x + r, y)
  c.arcTo(x + w, y, x + w, y + h, r)
  c.arcTo(x + w, y + h, x, y + h, r)
  c.arcTo(x, y + h, x, y, r)
  c.arcTo(x, y, x + w, y, r)
  c.closePath()
}
